<script>
export default {
	name: 'CheckIn',
	inheritAttrs: false,
};
</script>

<script setup>
import CTAButton from '~/aem-components/CTAButton.vue';
import MobileSubpage from '~/components/BookingWidget/MobileSubpage.vue';
import useBookingWidgetSubpage from '~/logic/composables/useBookingWidgetSubpage.js';
import TextField from '~/components/form/TextField.vue';
import Tooltip from '~/components/Tooltip.vue';
import FormWrapper from '~/components/form/FormWrapper.vue';
import yup from 'mh-yup';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { isMobileViewport } from '~/logic/composables/breakpoints.js';
import axios from 'axios';
import mhUpgradePopup from '../../components/mhUpgradePopup.vue';

const props = defineProps({
	bretnLabel: { type: String, default: '' },
	bretnMandatory: { type: Boolean, default: false },
	bretnPalceholder: { type: String, default: '' },
	bretnShortDescription: { type: String, default: '' },
	bretnCharLimit: { type: Number, default: 0 },
	bretnCharLimitErrorMessage: { type: String, default: '' },
	bretnMandatoryErrorMessage: { type: String, default: '' },

	checkInButtonLabel: { type: String, default: '' },
	checkInDescription: { type: String, default: '' },
	checkInNote: { type: String, default: '' },
	checkInToolTipLabel: { type: String, default: '' },
	checkInToolTip: { type: String, default: '' },

	lastNameLabel: { type: String, default: '' },
	lastNameMandatory: { type: Boolean, default: false },
	lastNameMandatoryErrorMessage: { type: String, default: '' },
	lastNamePalceholder: { type: String, default: '' },
	lastNameShortDescription: { type: String, default: '' },
	refxAPIURL: { type: String, default: '' },
	language: { type: String, default: '' },

	componentIdAA: { type: String, default: '' },

	enableMhUpgrade: { type: Boolean, default: false },
	imageConfig: { type: String, default: '' },
	textConfig: { type: String, default: '' },
	ctaYesBtnConfig: { type: String, default: 'Yes' },
	ctaNoBtnConfig: { type: String, default: 'No' },
	checkinMhUpgradeApiUrl: { type: String, default: '' },
	// Prop for HiddenSeamlessBookingWidget component
	isHideInDesktop: { type: Boolean, default: false },
	enableGlassDesign: { type: Boolean, default: false },
});

const { isEditorMode } = useGlobalAEMState();

const emit = defineEmits([
	// 'update:modelValue',
]);

const rootEl = ref(null);
const formRef = ref(null);
const bretnFieldValue = ref('');
const lastNameFieldValue = ref('');
const isPNREligibleForUpgrade = ref(false);
const isLoading = ref(false);
const upgradeOfferUrl = ref('');
const refxCheckInUrl = ref('');

const { isMobileSubpageVisible } = useBookingWidgetSubpage({
	hashKey: ['check-in', 'check-in-tab'],
	rootEl,
});

const bretnFieldValidation = computed(() => {
	if (props.bretnMandatory) {
		return yup
			.string()
			.required(props.bretnMandatoryErrorMessage)
			.max(props.bretnCharLimit, props.bretnCharLimitErrorMessage);
	}

	return null;
});

const lastNameFieldValidation = computed(() => {
	if (props.lastNameMandatory) {
		return yup
			.string()
			.required(props.lastNameMandatoryErrorMessage);
	}

	return null;
});

const handleSubmitValid = async (values, actions) => {
	try {
		if (props.enableMhUpgrade) {
			isLoading.value = true;
			const redirectUrl = `${props.refxAPIURL}?lang=${props.language}`;
			const response = await axios.get(props.checkinMhUpgradeApiUrl, {
				params: {
					ticketNumber: bretnFieldValue.value,
					lastName: lastNameFieldValue.value.toUpperCase(),
					redirectUrl: redirectUrl,
					component: 'checkin',
				},
			});

			const { offerEligibility, offerRedirectionUrl } = response.data || {};
			isPNREligibleForUpgrade.value = offerEligibility;
			upgradeOfferUrl.value = offerRedirectionUrl;

			console.log('✅ handleSubmitValid values = ', values);
		}
	} catch (error) {
		console.error('Error fetching PNR details:', error);
	} finally {
		// Ensure formSubmission is always called, even if there's an error
		formSubmission();
		isLoading.value = false;
	}
};


const handleSubmitInvalid = ({ values, errors, results, evt }) => {
	console.log('❌ handleSubmitInvalid errors = ', errors);
};

const formSubmission = () => {
	if (bretnFieldValue.value && lastNameFieldValue.value) {
		const params = {
			lastName: lastNameFieldValue.value,
			identifier: bretnFieldValue.value,
			...(props.language ? {
				lang: `${props.language}`,
			} : {}),
		};
		const url = new URL(props.refxAPIURL);
		Object.keys(params).forEach((key) => {
			url.searchParams.append(key, params[key]);
		});
		refxCheckInUrl.value = url.toString();
		if (!(props.enableMhUpgrade && isPNREligibleForUpgrade.value)) {
			window.location.href = refxCheckInUrl.value;
		}
	}
};

</script>

<template>
<div
	v-if="!props.isHideInDesktop"
	ref="rootEl"
	class="CheckIn booking-widget-subcomponent"
	:class="{
		'isEditorMode': isEditorMode,
	}"
>
	<div class="generic-container">
		<h5 v-if="isEditorMode" class="title-editor-mode">
			<icon-fas-plane class="mr-2 fill-current inline-block" />
			<span>Booking Widget: Check-in</span>
		</h5>

		<FormWrapper
			ref="formRef"
			class="check-in-wrapper"
			@submit-valid="handleSubmitValid"
			@submit-invalid="handleSubmitInvalid"
		>
			<p v-html-sanitize="props.checkInDescription"></p>
			<div class="check-in-tooltip flex items-center mt-6">
				<Tooltip>
					<template #default>
						<icon-fas-circle-question class="fill-primary-blue-base" />
					</template>
					<template #mobile-title>
						{{ props.checkInToolTipLabel }}
					</template>
					<template #tooltip-content>
						<p v-html-sanitize="props.checkInToolTip"></p>
					</template>
				</Tooltip>
				<span class="ml-3">{{ props.checkInToolTipLabel }}</span>
			</div>
			<div class="check-in-form mt-6 flex gap-x-6 md:flex-col md:gap-y-4">
				<div class="flex-grow">
					<TextField
						v-model="bretnFieldValue"
						:placeholder="props.bretnPalceholder"
						:ariaLabel="props.bretnLabel"
						:validation="bretnFieldValidation"
						showBtnClear
						name="bretnField"
						variant="booking-widget"
						:enableGlassDesign="props.enableGlassDesign"
					/>
					<template v-if="props.bretnShortDescription">
						<p
							v-if="!formRef?.errors['bretnField']"
							v-html-sanitize="props.bretnShortDescription"
							class="text-sm text-[#61666F] mt-1"
						></p>
					</template>
				</div>
				<div class="w-4/10 md:w-full">
					<TextField
						v-model="lastNameFieldValue"
						:placeholder="props.lastNamePalceholder"
						:ariaLabel="props.lastNameLabel"
						:validation="lastNameFieldValidation"
						:autoTransformToTitlecase="true"
						showBtnClear
						name="breLastNameField"
						variant="booking-widget"
						:enableGlassDesign="props.enableGlassDesign"
					/>
					<template v-if="props.lastNameShortDescription ">
						<p
							v-if="!formRef?.errors['breLastNameField']"
							v-html-sanitize="props.lastNameShortDescription"
							class="text-sm text-[#61666F] mt-1"
						></p>
					</template>
				</div>
				<div class="flex-none >lg:w-[max-content] md:mt-4 relative">
					<CTAButton
						v-if="!isLoading"
						v-aa="[
							{
								clickName: props.checkInButtonLabel,
								clickComponentType: 'Button',
								componentName: 'CheckIn',
								componentID: props.componentIdAA,
							},
							{
								name: props.checkInButtonLabel,
								type: 'exit',
							},
						]"
						:class="{ 'class-design-ct-btn': props.enableGlassDesign }"
						:ctaText="props.checkInButtonLabel"
						:disabled="!bretnFieldValue || !lastNameFieldValue"
						:isSubmit="true"
						:sizeType="isMobileViewport ? 'small' : 'large'"
						ctaSizing="fluid"
					/>
					<CTAButton
						v-else
						:ctaText="props.checkInButtonLabel"
						:disabled="true"
						:sizeType="isMobileViewport ? 'small' : 'large'"
						ctaSizing="fluid"
					/>
					<div
						v-if="isLoading"
						class="absolute loader-position"
					>
						<img src="..\..\assets\loader-sm.gif" alt="" />
					</div>

				</div>
			</div>
		</FormWrapper>

		<div
			v-if="props.checkInNote"
			class="flex mt-6"
		>
			<div class="rounded-xl bg-primary-blue-extralight flex-grow px-5 py-4 flex">
				<div class="basis-6 flex-none mr-3 rtl:ml-3 rtl:mr-0 top-0.5 relative">
					<icon-fas-circle-info class="fill-primary-blue-base" aria-hidden="true" />
				</div>
				<div class="typo-body-text-2">
					<span v-html-sanitize="props.checkInNote"></span>
				</div>
			</div>
		</div>
	</div>
</div>
<MobileSubpage
	:isVisible="isMobileSubpageVisible && !props.enableGlassDesign"
	class="text-white"
	style="--overlay-container-bg-color: var(--secondary-blue-extradark);"
	@update:is-visible="isMobileSubpageVisible = $event"
>

	<template #banner-title>
		<h1 class="text-center">{{ $t('Check In') }}</h1>
	</template>

	<template #default>
		<div class="generic-container my-4">
			<h5 v-if="isEditorMode" class="title-editor-mode">
				<icon-fas-plane class="mr-2 fill-current inline-block" />
				<span>Booking Widget: Check-in</span>
			</h5>

			<FormWrapper
				ref="formRef"
				class="check-in-wrapper"
				@submit-valid="handleSubmitValid"
				@submit-invalid="handleSubmitInvalid"
			>
				<p v-html-sanitize="props.checkInDescription" class="text-sm"></p>
				<div class="check-in-tooltip flex items-center mt-6">
					<Tooltip>
						<template #default>
							<icon-fas-circle-question class="fill-primary-blue-base" />
						</template>
						<template #mobile-title>
							{{ props.checkInToolTipLabel }}
						</template>
						<template #tooltip-content>
							<p v-html-sanitize="props.checkInToolTip"></p>
						</template>
					</Tooltip>
					<span class="ml-3 text-sm">{{ props.checkInToolTipLabel }}</span>
				</div>
				<div class="check-in-form mt-6 flex gap-x-6 md:gap-y-4 xl:flex-wrap lg:flex-col">
					<div class="flex-grow">
						<TextField
							v-model="bretnFieldValue"
							:placeholder="props.bretnPalceholder"
							:ariaLabel="props.bretnLabel"
							:validation="bretnFieldValidation"
							showBtnClear
							name="bretnField"
							variant="booking-widget"
							:enableGlassDesign="props.enableGlassDesign"
						/>
						<template v-if="props.bretnShortDescription">
							<p
								v-if="!formRef?.errors['bretnField']"
								v-html-sanitize="props.bretnShortDescription"
								class="text-sm text-[#61666F] mt-1"
							></p>
						</template>
					</div>
					<div class="w-full">
						<TextField
							v-model="lastNameFieldValue"
							:placeholder="props.lastNamePalceholder"
							:ariaLabel="props.lastNameLabel"
							:validation="lastNameFieldValidation"
							:autoTransformToTitlecase="true"
							showBtnClear
							name="breLastNameField"
							variant="booking-widget"
							:enableGlassDesign="props.enableGlassDesign"
						/>
						<template v-if="props.lastNameShortDescription ">
							<p
								v-if="!formRef?.errors['breLastNameField']"
								v-html-sanitize="props.lastNameShortDescription"
								class="text-sm text-[#61666F] mt-1"
							></p>
						</template>
					</div>
					<div class="flex-none >lg:w-[max-content] mt-4 relative">
						<CTAButton
							v-if="!isLoading"
							v-aa="[
								{
									clickName: props.checkInButtonLabel,
									clickComponentType: 'Button',
									componentName: 'CheckIn',
									componentID: props.componentIdAA,
								},
								{
									name: props.checkInButtonLabel,
									type: 'exit',
								},
							]"
							:ctaText="props.checkInButtonLabel"
							:disabled="!bretnFieldValue || !lastNameFieldValue"
							:isSubmit="true"
							:sizeType="isMobileViewport ? 'small' : 'large'"
							ctaSizing="fluid"
							:class="{ 'class-design-ct-btn': props.enableGlassDesign }"
						/>
						<CTAButton
							v-else
							:ctaText="props.checkInButtonLabel"
							:disabled="true"
							:sizeType="isMobileViewport ? 'small' : 'large'"
							ctaSizing="fluid"
						/>
						<div
							v-if="isLoading"
							class="absolute loader-position"
						>
							<img src="..\..\assets\loader-sm.gif" alt="" />
						</div>
					</div>
				</div>
			</FormWrapper>

			<div
				v-if="props.checkInNote"
				class="flex mt-6"
			>
				<div class="rounded-xl bg-primary-blue-extralight flex-grow px-5 py-4 flex">
					<div class="basis-6 flex-none mr-3 rtl:ml-3 rtl:mr-0 top-0.5 relative">
						<icon-fas-circle-info class="fill-primary-blue-base" aria-hidden="true" />
					</div>
					<div class="typo-body-text-2">
						<span v-html-sanitize="props.checkInNote"></span>
					</div>
				</div>
			</div>
		</div>
	</template>

</MobileSubpage>

<mhUpgradePopup
	v-if="isPNREligibleForUpgrade"
	:enableMhUpgrade="props.enableMhUpgrade"
	:image="props.imageConfig"
	:imageAltText="props.imageAltText"
	:promotionBody="props.textConfig"
	:ctaYesButtonLabel="props.ctaYesBtnConfig"
	:ctaNoButtonLabel="props.ctaNoBtnConfig"
	:ctaYesButtonPath="upgradeOfferUrl"
	:ctaNoButtonPath="refxCheckInUrl"
	:isManageBooking="false"
>
</mhUpgradePopup>



</template>

<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.CheckIn {
	// no need to make this align with its parent switch tab, for now
	/* --tab-width: 205px;
	--tab-count: 6;

	max-width: calc(var(--tab-width) * var(--tab-count) * 0.92);
	margin: 0 auto; */
}

.loader-position {
	width: 50px;
    top: 9%;
    left: 35%;

	@media #{var.$query-max-md} {
		top: 0%;
		left: 43%;
	}
}

</style>
